import { useState, useCallback, useMemo, lazy, Suspense } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { Pen, MousePointer2, Ruler } from 'lucide-react';
import { useSeatCushionSlotStore } from '@/store/seatCushionSlotStore';
import { useMaterialsLibrary } from '@/api/materials-library';
import { useDrawingViewport } from '@/api/drawing-viewports';
import { usePageTileInfo } from '@/api/knowledge';
import SeatCushionSlotList from './SeatCushionSlotList';
import ScaleCalibration from '../../markup-viewer/ScaleCalibration';
import ScalePillsBar from '../../markup-viewer/ScalePillsBar';
import type { Markup, MaterialLibrary, BanquettePolyline } from '@estimator/shared';

const TileViewer = lazy(() => import('../../markup-viewer/TileViewer'));

type SlotTool = 'select' | 'polyline' | 'calibrate';

interface SeatCushionSlotManagerProps {
  isOpen: boolean;
  onClose: () => void;
  viewportId: string;
  projectId: string;
  productType?: string;
  profileDepth?: number;
  profileHeight?: number;
}

export default function SeatCushionSlotManager({
  isOpen, onClose, viewportId, projectId, productType, profileDepth, profileHeight,
}: SeatCushionSlotManagerProps) {
  const [tool, setTool] = useState<SlotTool>('polyline');
  const { data: viewport } = useDrawingViewport(viewportId);
  const { data: materials = [] } = useMaterialsLibrary();

  const { slots, activeSlotId, addPolylineToSlot } = useSeatCushionSlotStore();
  const activeSlot = activeSlotId ? slots.find((s) => s.id === activeSlotId) : null;
  const isPolylineRole = (activeSlot?.panel_role ?? 'front') === 'front';

  const pageId = viewport?.page_id ?? null;
  const knowledgeItemId = viewport?.page_id ?? '';
  const bounds = viewport?.bounds as [number, number, number, number] | undefined;

  const { data: tileInfo } = usePageTileInfo(pageId);

  const displayMarkups: Markup[] = useMemo(() => {
    return slots.flatMap((slot) =>
      slot.polylines.map((pl, i) => ({
        id: `${slot.id}-pl-${i}`,
        project_id: projectId,
        knowledge_item_id: viewport?.page_id ?? '',
        page_id: viewport?.page_id ?? '',
        geometry: { type: 'LineString' as const, coordinates: pl.points },
        measurement_type: 'polyline',
        measured_value: pl.length_real ?? pl.length_px,
        measured_unit: pl.length_unit ?? 'px',
        label: `${slot.name} #${i + 1}`,
        category: 'DIMENSION' as const,
        color: slot.color,
        sort_order: 0,
        created_at: '',
        updated_at: '',
      })) as Markup[],
    );
  }, [slots, projectId, viewport?.page_id]);

  const handleCreateMarkup = useCallback((data: any) => {
    if (!activeSlotId) return;
    const polyline: BanquettePolyline = {
      points: data.points ?? data.geometry?.coordinates ?? [],
      length_px: data.length_px ?? 0,
      length_real: data.length_real ?? null,
      length_unit: data.length_unit ?? null,
    };
    addPolylineToSlot(activeSlotId, polyline);
  }, [activeSlotId, addPolylineToSlot]);

  const effectiveTool =
    tool === 'calibrate'
      ? 'calibrate'
      : !activeSlotId
        ? 'select'
        : !isPolylineRole && tool === 'polyline'
          ? 'select'
          : tool;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-[var(--bg-base)]',
        header: 'border-b border-[var(--border)]',
        footer: 'border-t border-[var(--border)]',
      }}
    >
      <ModalContent>
        <ModalHeader className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--accent)' }}>
          Slot Manager — Draw Finish Profiles
        </ModalHeader>
        <ModalBody className="p-0 flex-1">
          <div className="flex h-full" style={{ minHeight: '70vh' }}>
            <div className="w-[260px] flex-shrink-0 border-r" style={{ borderColor: 'var(--border)' }}>
              <SeatCushionSlotList materials={materials as MaterialLibrary[]} productType={productType} profileDepth={profileDepth} profileHeight={profileHeight} />
            </div>

            <div className="flex-1 flex flex-col min-w-0">
              <div className="flex items-center gap-1 px-3 py-2 border-b" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-surface)' }}>
                <Button
                  size="sm"
                  color={tool === 'select' ? 'primary' : 'default'}
                  variant={tool === 'select' ? 'flat' : 'light'}
                  onPress={() => setTool('select')}
                  startContent={<MousePointer2 size={14} />}
                  isDisabled={!activeSlotId}
                >
                  Select
                </Button>
                <Button
                  size="sm"
                  color={tool === 'polyline' ? 'primary' : 'default'}
                  variant={tool === 'polyline' ? 'flat' : 'light'}
                  onPress={() => setTool('polyline')}
                  startContent={<Pen size={14} />}
                  isDisabled={!activeSlotId || !isPolylineRole}
                  title={
                    !activeSlotId
                      ? 'Select a slot first'
                      : !isPolylineRole
                        ? 'This slot is sized from product dimensions — no drawing needed'
                        : ''
                  }
                >
                  Draw Polyline
                </Button>
                <Button
                  size="sm"
                  color={tool === 'calibrate' ? 'primary' : 'default'}
                  variant={tool === 'calibrate' ? 'flat' : 'light'}
                  onPress={() => setTool('calibrate')}
                  startContent={<Ruler size={14} />}
                >
                  Calibrate Scale
                </Button>
                <div className="flex-1" />
                {!activeSlotId && tool !== 'calibrate' && (
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Select a slot to start drawing
                  </span>
                )}
                {tool === 'calibrate' && (
                  <span className="text-xs" style={{ color: 'var(--accent)' }}>
                    Draw a line over a known dimension, then enter the real measurement
                  </span>
                )}
                {activeSlotId && tool !== 'calibrate' && (
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Drawing on: <span style={{ color: slots.find(s => s.id === activeSlotId)?.color }}>{slots.find(s => s.id === activeSlotId)?.name}</span>
                  </span>
                )}
              </div>

              {pageId && tileInfo && (
                <ScalePillsBar
                  pageId={pageId}
                  currentScale={tileInfo.scalePixelsPerUnit}
                  currentUnit={tileInfo.scaleUnit || 'ft'}
                />
              )}

              <div className="flex-1 relative">
                {pageId ? (
                  <Suspense fallback={
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
                    </div>
                  }>
                    <TileViewer
                      pageId={pageId}
                      markups={displayMarkups}
                      projectId={projectId}
                      knowledgeItemId={knowledgeItemId}
                      onCreateMarkup={handleCreateMarkup}
                      mode="slot"
                      activeToolOverride={effectiveTool}
                      viewportBounds={bounds}
                    />
                  </Suspense>
                ) : (
                  <div className="flex items-center justify-center h-full text-default-400">
                    No viewport linked. Close and select a drawing viewport first.
                  </div>
                )}

                {pageId && tileInfo && (
                  <div className="absolute bottom-4 right-4 z-10">
                    <ScaleCalibration
                      pageId={pageId}
                      currentScale={tileInfo.scalePixelsPerUnit}
                      currentUnit={tileInfo.scaleUnit || 'ft'}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            {slots.length} slot{slots.length !== 1 ? 's' : ''} &middot;{' '}
            {slots.reduce((sum, s) => sum + s.polylines.length, 0)} polyline{slots.reduce((sum, s) => sum + s.polylines.length, 0) !== 1 ? 's' : ''}
          </div>
          <div className="flex-1" />
          <Button variant="light" onPress={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
